/**
 * WHOOP Integration Card for Dashboard
 * Always shows cached data with last synced time and refresh option
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Activity, Heart, Moon, Zap, CheckCircle2, ArrowRight, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWhoopDataCached } from '@/hooks/useWhoopDataCached';
import { useWhoopSyncContext } from '@/components/providers/WhoopSyncProvider';
import { formatDistanceToNow } from 'date-fns';

export const WhoopDashboardCard: React.FC = () => {
  const navigate = useNavigate();
  const { 
    isLoading: loading, 
    isConnected, 
    latestCompleteData: todayData, 
    isFetching,
    lastSync,
    data,
    refresh 
  } = useWhoopDataCached(7);
  const { isSyncing, triggerSync } = useWhoopSyncContext();

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerSync();
    refresh();
  };

  // Format last sync time
  const getLastSyncText = () => {
    if (!lastSync) return null;
    try {
      return formatDistanceToNow(new Date(lastSync), { addSuffix: true });
    } catch {
      return null;
    }
  };

  const lastSyncText = getLastSyncText();
  const isRefreshing = isSyncing || isFetching;

  // Loading only on first load with no cached data
  if (loading && !todayData && data.length === 0) {
    return (
      <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
        <CardContent className="p-6">
          <div className="animate-pulse flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-muted rounded w-1/3" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Connected state - always show data if we have any (even stale)
  if (isConnected && (todayData || data.length > 0)) {
    const displayData = todayData || data[0];
    
    return (
      <Card 
        className="border-border/50 bg-gradient-to-br from-card via-card to-primary/5 backdrop-blur-sm overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300"
        onClick={() => navigate('/whoop-dashboard')}
      >
        <CardContent className="p-5 sm:p-6">
          {/* Header with branding */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                <Activity className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground tracking-tight">WHOOP</span>
                  <span className="text-muted-foreground">×</span>
                  <span className="font-medium text-primary">Therappi<sup className="text-[0.6em]">®</sup></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-primary" />
                  <span className="text-xs text-foreground/80">
                    {lastSyncText ? `Synced ${lastSyncText}` : 'Connected'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50"
              aria-label="Refresh WHOOP data"
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            <div className="text-center py-3 px-1.5 sm:p-3 rounded-lg bg-accent/10 min-h-[72px] flex flex-col justify-center">
              <div className="text-2xl sm:text-4xl font-bold text-accent tracking-tight leading-tight">
                {displayData?.recovery_score ? Math.round(displayData.recovery_score) : '--'}
                {displayData?.recovery_score && <span className="text-xs sm:text-sm font-semibold text-accent/70">%</span>}
              </div>
              <div className="text-[10px] sm:text-xs font-medium text-foreground/70 uppercase tracking-wide mt-1">Recovery</div>
            </div>
            <div className="text-center py-3 px-1.5 sm:p-3 rounded-lg bg-primary/10 min-h-[72px] flex flex-col justify-center">
              <div className="text-2xl sm:text-4xl font-bold text-primary tracking-tight leading-tight">
                {displayData?.hrv_score ? Math.round(displayData.hrv_score) : '--'}
                <span className="text-xs sm:text-sm font-semibold text-primary/70">ms</span>
              </div>
              <div className="text-[10px] sm:text-xs font-medium text-foreground/70 uppercase tracking-wide mt-1">HRV</div>
            </div>
            <div className="text-center py-3 px-1.5 sm:p-3 rounded-lg bg-secondary/10 min-h-[72px] flex flex-col justify-center">
              <div className="text-2xl sm:text-4xl font-bold text-secondary tracking-tight leading-tight">
                {displayData?.sleep_duration_minutes 
                  ? (displayData.sleep_duration_minutes / 60).toFixed(1) 
                  : '--'}
                {displayData?.sleep_duration_minutes && <span className="text-xs sm:text-sm font-semibold text-secondary/70">h</span>}
              </div>
              <div className="text-[10px] sm:text-xs font-medium text-foreground/70 uppercase tracking-wide mt-1">Sleep</div>
            </div>
            <div className="text-center py-3 px-1.5 sm:p-3 rounded-lg bg-muted/20 min-h-[72px] flex flex-col justify-center">
              <div className="text-2xl sm:text-4xl font-bold text-foreground tracking-tight leading-tight">
                {displayData?.strain_score ? displayData.strain_score.toFixed(1) : '--'}
              </div>
              <div className="text-[10px] sm:text-xs font-medium text-foreground/70 uppercase tracking-wide mt-1">Strain</div>
            </div>
          </div>

          {/* Tap to view more hint */}
          <div className="mt-3 flex items-center justify-center gap-1 text-xs text-muted-foreground">
            <span>Tap for insights</span>
            <ArrowRight className="w-3 h-3" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Connected but no data yet
  if (isConnected) {
    return (
      <Card 
        className="border-border/50 bg-gradient-to-br from-card via-card to-primary/5 backdrop-blur-sm overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300"
        onClick={() => navigate('/whoop-dashboard')}
      >
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                <Activity className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">WHOOP</span>
                  <span className="text-muted-foreground">×</span>
                  <span className="font-medium text-primary">Therappi<sup className="text-[0.6em]">®</sup></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-primary" />
                  <span className="text-xs text-foreground/80">Connected · Syncing data...</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Not connected - show promotional card
  return (
    <Card className="border-border/50 bg-gradient-to-br from-card via-card to-primary/5 backdrop-blur-sm overflow-hidden">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          {/* Logo/Icon */}
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center flex-shrink-0">
            <Activity className="w-6 h-6 text-primary-foreground" />
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Branding */}
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-foreground">WHOOP</span>
              <span className="text-muted-foreground">×</span>
              <span className="font-medium text-primary">Therappi<sup className="text-[0.6em]">®</sup></span>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">NEW</Badge>
            </div>
            
            <p className="text-sm text-muted-foreground mb-3">
              Connect your WHOOP to unlock mind-body insights and auto-populate check-ins.
            </p>
            
            {/* Benefits */}
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="flex items-center gap-1 text-xs text-foreground/70">
                <Heart className="w-3 h-3 text-secondary" />
                <span>HRV Tracking</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-foreground/70">
                <Moon className="w-3 h-3 text-primary" />
                <span>Sleep Analysis</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-foreground/70">
                <Zap className="w-3 h-3 text-accent" />
                <span>Recovery Scores</span>
              </div>
            </div>
            
            <Button 
              onClick={() => navigate('/whoop-dashboard')}
              size="sm"
              className="w-full sm:w-auto"
            >
              Connect WHOOP
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
